// ABOUTME: Which session-change dialog is open app-wide: none, the picker to move, or the cancel
// ABOUTME: Held above the pages so a refresh cannot unmount a result; refreshes run on closing
import { Maybe } from 'maybeasy';
import { action, makeObservable, observable } from 'mobx';
import { Link } from '../../Resource/Types';
import { CancelTarget } from './CancelTarget';

// What the page that opened the dialog refreshes once a change succeeds, if anything.
type Refresh = Maybe<() => void>;

export type SessionChangeDialog =
  | { kind: 'closed' }
  | { kind: 'reschedule'; link: Link; refresh: Refresh; changed: boolean }
  | { kind: 'cancel'; target: CancelTarget; refresh: Refresh; changed: boolean };

class SessionChangeStore {
  @observable
  dialog: SessionChangeDialog = { kind: 'closed' };

  constructor() {
    makeObservable(this);
  }

  @action
  openReschedule = (link: Link, refresh: Refresh) => {
    this.dialog = { kind: 'reschedule', link, refresh, changed: false };
  };

  @action
  openCancel = (target: CancelTarget, refresh: Refresh) => {
    this.dialog = { kind: 'cancel', target, refresh, changed: false };
  };

  // The session moved or was cancelled; the page refreshes once the result is closed, so the
  // refresh cannot pull the result out from under the person reading it.
  @action
  changed = () => {
    switch (this.dialog.kind) {
      case 'closed': {
        break;
      }
      case 'reschedule':
      case 'cancel': {
        this.dialog = { ...this.dialog, changed: true };
        break;
      }
    }
  };

  // Closes the dialog and runs the opener's refresh, if the session changed; also returns whether
  // it changed so the host can refresh what it owns.
  @action
  close = (): boolean => {
    const dialog = this.dialog;
    this.dialog = { kind: 'closed' };
    switch (dialog.kind) {
      case 'closed': {
        return false;
      }
      case 'reschedule':
      case 'cancel': {
        if (dialog.changed) {
          dialog.refresh.do((refresh) => {
            refresh();
          });
        }
        return dialog.changed;
      }
    }
  };
}

export const sessionChangeStore = new SessionChangeStore();
